import { SensorLockerStatus } from '@frontend/device/types';
import { PackageClient } from '@frontend/package/api';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useSlotListener } from '../../../hooks/useSlotListener/useSlotListener';
import { useWizardPackages } from '../../hooks/useWizardPackages';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading?: boolean;

    slots: Slot[] | null;
    openSlots: Slot[];
    errorSlots: Slot[];
    closedSlots: Slot[];
    otherSlots: Slot[];
    slotStatusMap: Map<string, SensorLockerStatus> | null;
    allSlotsClosed: boolean;
    showClosedSlots: boolean;
    toggleShowClosedSlots: () => void;

    nextStep?: Step;
    submitDisabled?: boolean;
    submit: () => void;
    continueEnabled: boolean;
    onContinue: () => void;
    showCenterContinue: boolean;
    onCenterContinue: () => void;
}

const useProcessOpenCloseSlots = (): ViewProps => {
    const { navigationState, store, onSubmit, continueEnabled, onContinue, onHome } = useScreen();
    const { remoteTransaction } = useWizardTransaction({
        wizardId: store!.wizard.id,
        store,
        autoFetch: true
    });
    const { packages } = useWizardPackages({
        wizardId: store!.wizard.id,
        store,
        autoFetch: true
    });
    const [relevantPackages, setRelevantPackages] = useState<typeof packages | null>(null);
    const [slotStatusMap, setSlotStatusMap] = useState<Map<string, SensorLockerStatus> | null>(null);
    const slotStatusMapRef = useRef<Map<string, SensorLockerStatus>>(new Map());
    const [slots, setSlots] = useState<Slot[] | null>(null);
    const [showClosedSlots, setShowClosedSlots] = useState<boolean>(true);

    const toggleShowClosedSlots = () => {
        setShowClosedSlots((prev) => !prev);
    };

    // Initialize slot status map when packages change
    useEffect(() => {
        if (relevantPackages === null) {
            if (packages && packages.length > 0) {
                setRelevantPackages(packages);
            } else if (remoteTransaction) {
                PackageClient.fetchAccountTransactionPackages(remoteTransaction.account_id, remoteTransaction.id, {
                    spot_id: navigationState.iot?.spot_id
                }).then((response) => {
                    setRelevantPackages(response.results);
                });
            }
        } else if (slotStatusMap === null) {
            const statusMap = new Map<string, SensorLockerStatus>();
            relevantPackages.forEach((pkg) => {
                if (pkg.slot_id) {
                    statusMap.set(pkg.slot_id, SensorLockerStatus.OPENING);
                }
            });
            setSlotStatusMap(statusMap);
            slotStatusMapRef.current = statusMap;
        }

        if (relevantPackages && slots === null) {
            SlotClient.resolveSlots(relevantPackages.filter((pkg) => pkg.slot_id).map((pkg) => pkg.slot_id!)).then((response) => {
                setSlots(response.results);
            });
        }
    }, [relevantPackages, remoteTransaction, packages, slotStatusMap]);

    useSlotListener({
        id: 'PROCESS_OPEN_CLOSE_SLOTS',
        onPackageSlotNotClosed(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
            }
        },
        onPackageSlotNotOpened(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
            }
        },
        onPackageSlotOpened(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.OPENED);
            }
        },
        onPackageSlotClosed(pkg, data) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.CLOSED);
            }
        }
    });

    // Check if all slots are closed
    const allSlotsClosed = useMemo(() => {
        if (!slotStatusMap) return false;
        if (slotStatusMap.size === 0) return false;
        return Array.from(slotStatusMap.values()).every((status) => status === SensorLockerStatus.CLOSED);
    }, [slotStatusMap]);

    // Sort slots by status priority
    const { sortedSlots, openSlots, errorSlots, closedSlots, otherSlots } = useMemo(() => {
        if (!slots || !slotStatusMap)
            return {
                sortedSlots: [],
                openSlots: [],
                errorSlots: [],
                closedSlots: [],
                otherSlots: []
            };

        const openSlots: Slot[] = [];
        const errorSlots: Slot[] = [];
        const closedSlots: Slot[] = [];
        const otherSlots: Slot[] = [];

        const getStatusPriority = (status: SensorLockerStatus | undefined): number => {
            switch (status) {
                case SensorLockerStatus.OPENED:
                case SensorLockerStatus.OPENING:
                    return 0;
                case SensorLockerStatus.NOT_OPENED:
                case SensorLockerStatus.ERROR:
                    return 1;
                case SensorLockerStatus.CLOSED:
                    return 2;
                default:
                    return 3;
            }
        };

        const sortedSlots = slots.slice().sort((a, b) => {
            const priorityA = getStatusPriority(slotStatusMap.get(a.id));
            const priorityB = getStatusPriority(slotStatusMap.get(b.id));
            return priorityA - priorityB;
        });

        sortedSlots.forEach((slot) => {
            const status = slotStatusMap.get(slot.id);
            switch (status) {
                case SensorLockerStatus.OPENED:
                case SensorLockerStatus.OPENING:
                    openSlots.push(slot);
                    break;
                case SensorLockerStatus.NOT_OPENED:
                case SensorLockerStatus.ERROR:
                    errorSlots.push(slot);
                    break;
                case SensorLockerStatus.CLOSED:
                    closedSlots.push(slot);
                    break;
                default:
                    otherSlots.push(slot);
                    break;
            }
        });

        return { sortedSlots, openSlots, errorSlots, closedSlots, otherSlots };
    }, [slots, slotStatusMap]);

    const submit = async () => {
        if (!store) return;
        const nextStep = store.steps.find((step) => step.screen === ScreenType.PROCESS_OPEN_CLOSE_SLOTS && step.trigger === ScreenTrigger.SUBMIT);
        onSubmit(nextStep);
    };

    const isLoading = slotStatusMap === null || relevantPackages === null || slots === null;
    return {
        isLoading,
        slots: sortedSlots,
        openSlots,
        errorSlots,
        closedSlots,
        otherSlots,
        slotStatusMap,
        allSlotsClosed,
        showClosedSlots,
        toggleShowClosedSlots,
        nextStep: store?.steps.find((step) => step.screen === ScreenType.PROCESS_OPEN_CLOSE_SLOTS && step.trigger === ScreenTrigger.SUBMIT),
        submitDisabled: !allSlotsClosed || isLoading,
        submit,
        continueEnabled,
        onContinue,
        showCenterContinue: continueEnabled || !!navigationState.user,
        onCenterContinue: continueEnabled ? onContinue : onHome
    };
};

export default useProcessOpenCloseSlots;
